import * as React from "react";

import {
    Context,
    RenderEnvironment,
    ValueProducer,
    PotentialValue,
    ActionSource,
    LOADING,
    AppEnvironment,
} from "common/components/types";
import { asBoolean, isLoadedDefined } from "common/components/component-helpers";
import { CodeScannerStandard } from "common/code-scanner-standards";
import { LiveLinearBarcodeScanner } from "webapp/ui/smart/live-linear-barcode-scanner/live-linear-barcode-scanner";
import { GlideFunctionalRenderer } from "common/components/uis";
import { useForce } from "hooks/use-force";
import { useForceValueProducers } from "hooks/use-value-producers";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { QuotaKind } from "common/Database/quotas";
import { definedMap, dontAwait } from "@glideapps/ts-necessities";
import { ConfirmModalStyle, showConfirmModal } from "common/components/confirm-modal";
import { getAnyLocalizedString, getLocalizedString } from "common/components/localization";

type Props = Readonly<{
    onSuccessPV: PotentialValue;
    outputVP: ValueProducer;
    ctx: Context;
    env: RenderEnvironment;
    appEnvironment: AppEnvironment;
}>;

type InternedScanResult = Readonly<{ standard: CodeScannerStandard; value: string }>;

function LiveScannerMaterial({ onSuccessPV, outputVP, ctx, env, appEnvironment }: Props): JSX.Element {
    const [scanResult, setScanResult] = React.useState<InternedScanResult | undefined>();
    const { onClick: onSuccess } = useOnClickFromAction(onSuccessPV, ActionSource.None, ctx, env);

    const handleValue = React.useCallback(
        (standard: CodeScannerStandard, value: string) => {
            // We only want to fire once per unique value. If we're continuously scanning
            // and looking at the same value for a while, we don't want to intentionally
            // re-trigger until the underlying code has changed.
            if (scanResult !== undefined && scanResult.standard === standard && scanResult.value === value) {
                return;
            }

            setScanResult({ standard, value });
            const quotaValue = appEnvironment.getQuotaValues?.(QuotaKind.BarcodesScanned);
            if (quotaValue?.limit !== undefined && quotaValue.current >= quotaValue.limit) {
                return;
            }

            // The scanner sends a standard of "None" whenever there are enough empty scans
            // to justify resetting the last scan result.
            if (standard !== CodeScannerStandard.None) {
                outputVP.set(value);
                appEnvironment.reportQuotaIncrease(QuotaKind.BarcodesScanned, 1);
                // FIXME: We should have been able to thread through the ActionSource here.
                const f = onSuccess?.(undefined);
                definedMap(f, dontAwait);
            }
        },
        [outputVP, onSuccess, scanResult, setScanResult, appEnvironment]
    );

    return <LiveLinearBarcodeScanner onScannedCode={handleValue} />;
}

export const LiveLinearBarcodeScannerMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[, onSuccessPV], [outputVP]] = useForceValueProducers(getValueProducers, force, ctx);
    const { appEnvironment } = env;

    const quotaVP = appEnvironment?.getQuotaReachedFlag(QuotaKind.BarcodesScanned);
    const quotaReached = asBoolean(definedMap(quotaVP, force));

    React.useEffect(() => {
        if (onSuccessPV === LOADING || !isLoadedDefined(outputVP) || appEnvironment === undefined) {
            return;
        }

        if (quotaReached) {
            dontAwait(
                showConfirmModal({
                    title: getAnyLocalizedString("scanQuotaModalHeader"),
                    description: getAnyLocalizedString("scanQuotaModalBody"),
                    accept: getLocalizedString("ok"),
                    cancel: getLocalizedString("support"),
                    style: ConfirmModalStyle.QuotaWarning,
                }).then(clickedOK => {
                    // We're a modal screen, not a regular screen. The distinction
                    // is important: we don't want to pop the regular screen,
                    // and in many cases we couldn't anyway!
                    appEnvironment.popModalScreen(env);
                    if (clickedOK === false) {
                        appEnvironment.pushFeedbackScreen(env);
                    }
                })
            );
        }
    }, [quotaReached, onSuccessPV, outputVP, appEnvironment, env]);

    if (onSuccessPV === LOADING || !isLoadedDefined(outputVP) || appEnvironment === undefined) {
        return null;
    }

    return (
        <LiveScannerMaterial
            onSuccessPV={onSuccessPV}
            outputVP={outputVP}
            ctx={ctx}
            env={env}
            appEnvironment={appEnvironment}
        />
    );
};
